/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import { When } from "react-if";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  ExtendedTooltip,
  FlexContainer,
  Input,
  SVGIcon,
  theme,
} from "@nordcloud/gnui";
import { useEstateSearchLazyQuery } from "~/generated/graphql";
import { ButtonTooltip } from "~/components";
import { ROUTES } from "~/routing/routes";
import { extendedEncodeURI } from "~/tools";
import { SearchBoxResults } from "./SearchBoxResults";

type Props = {
  isOpen: boolean;
  onToggleOpen: () => void;
};

export function SearchBox({ isOpen, onToggleOpen }: Props) {
  const [phrase, setPhrase] = useState("");
  const [showResults, setShowResults] = useState(false);

  const [search, { data, loading, error }] = useEstateSearchLazyQuery();

  const navigate = useNavigate();

  const onInput = (event: ChangeEvent<HTMLInputElement>) => {
    setPhrase(event.target.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      navigate(
        `${ROUTES.estateRecords.index}?query=${extendedEncodeURI(phrase)}`
      );
      setShowResults(false);
    }
  };

  useEffect(() => {
    if (phrase.length > 1) {
      const searchTimer = setTimeout(() => {
        const path = `/estate?query=${extendedEncodeURI(phrase)}`;
        if (!window.location.pathname.includes(path)) {
          void search({ variables: { query: phrase, limit: 5, page: 0 } });
          setShowResults(true);
        }
      }, 1000);

      return () => {
        clearInterval(searchTimer);
      };
    }
  }, [phrase, search, setShowResults]);

  const results = data?.estateV2?.resources ?? [];

  const showBoxWithResults = showResults && !loading;

  return (
    <SearchWrapper isOpen={isOpen}>
      <div className="search-toggle">
        <ExtendedTooltip placement="bottom" caption="Search" position="center">
          <ButtonTooltip onClick={onToggleOpen}>
            <SVGIcon name="search" />
          </ButtonTooltip>
        </ExtendedTooltip>
      </div>
      <section
        css={{ position: "relative", flexBasis: "100%" }}
        className="search-input-wrapper"
      >
        <FlexContainer
          columnGap={theme.spacing.spacing01}
          css={{ width: "100%", "> div:first-of-type": { width: "100%" } }}
        >
          <Input
            name="search-input"
            placeholder="Search"
            type="search"
            value={phrase}
            loading={loading}
            onChange={(event) => onInput(event)}
            onKeyDown={(event) => onKeyDown(event)}
          />
          <section className="close-button">
            <ExtendedTooltip
              placement="bottom"
              caption="Close Search"
              position="center"
            >
              <ButtonTooltip onClick={onToggleOpen}>
                <SVGIcon name="close" size="sm" />
              </ButtonTooltip>
            </ExtendedTooltip>
          </section>
        </FlexContainer>
        <When condition={showBoxWithResults}>
          <SearchBoxResults
            phrase={phrase}
            results={results}
            loading={loading}
            error={error}
            onSetShowResults={setShowResults}
          />
        </When>
      </section>
    </SearchWrapper>
  );
}

const SearchWrapper = styled.div<{ isOpen: boolean }>`
  width: 100%;
  display: flex;

  .search-toggle,
  .close-button {
    display: none;
  }

  @media screen and (max-width: ${theme.breakpoints.xl}px) {
    width: unset;

    .close-button {
      display: block;
    }

    .search-toggle {
      display: ${({ isOpen }) => (isOpen ? "none" : "inline-block")};
    }

    .search-input-wrapper {
      display: ${({ isOpen }) => (isOpen ? "block" : "none")};
    }
  }
`;
